import { useState } from 'react';
import type { DietaryConcern } from '../../shared/menu';
import { languageName } from '../../shared/languages';
import { dietaryQuestionFor, DIETARY_TAG_LABELS } from '../lib/dietaryPhrasebook';
import { WaiterQuestionOverlay } from './WaiterQuestionOverlay';

interface DietaryQuestionChipProps {
  concern: DietaryConcern;
  sourceLanguage: string;
}

// Rendered under each tagged cart line on the hand-off screen.
//
// - Preference (yellow): compact chip, question truncated, tap to expand overlay.
// - Strict (red):       prominent card with the full source-language question
//                       visible by default, so the waiter reads it at a glance.
//                       Still tappable to open the full-screen overlay.
//
// For languages without a native-QA'd phrasebook entry, we fall back to English
// with a visible "shown in English" warning — the traveler sees that the phrase
// isn't translated and can take extra care (point at dish, gesture, use app).
export function DietaryQuestionChip({ concern, sourceLanguage }: DietaryQuestionChipProps) {
  const [open, setOpen] = useState(false);

  const result = dietaryQuestionFor(concern.tag, concern.severity, sourceLanguage, concern.reason);
  if (!result) return null;

  const question = result.text;
  const isStrict = concern.severity === 'strict';
  const glossResult = dietaryQuestionFor(concern.tag, concern.severity, 'en', concern.reason);
  const englishGloss = glossResult?.text ?? DIETARY_TAG_LABELS[concern.tag];
  const fallbackNote = result.translated
    ? null
    : `Shown in English — ${languageName(sourceLanguage)} translation coming soon.`;

  if (isStrict) {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            marginTop: 8,
            padding: '10px 14px',
            borderRadius: 10,
            border: '2px solid #C4332B',
            background: 'rgba(196,51,43,0.08)',
            color: '#C4332B',
            font: 'inherit',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Allergy / Medical
          </div>
          <p lang={result.lang} style={{ margin: '6px 0 0', fontSize: 15, lineHeight: 1.35, color: '#0A0A0A', fontWeight: 500 }}>
            {question}
          </p>
          {fallbackNote && (
            <p style={{ margin: '6px 0 0', fontSize: 11, lineHeight: 1.35, color: '#7A2018', fontStyle: 'italic' }}>
              {fallbackNote}
            </p>
          )}
        </button>
        <WaiterQuestionOverlay
          open={open}
          question={question}
          questionLang={result.lang}
          sourceLanguage={sourceLanguage}
          translated={result.translated}
          severity={concern.severity}
          englishGloss={englishGloss}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  // Preference — compact chip.
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          marginTop: 8,
          borderRadius: 9999,
          border: '1.5px solid #B98300',
          background: 'rgba(185,131,0,0.08)',
          color: '#B98300',
          fontSize: 13,
          fontWeight: 500,
          maxWidth: '100%',
        }}
      >
        <span aria-hidden style={{ fontSize: 14, fontWeight: 700 }}>{fallbackNote ? 'EN' : '?'}</span>
        <span lang={result.lang} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {question}
        </span>
      </button>
      <WaiterQuestionOverlay
        open={open}
        question={question}
        questionLang={result.lang}
        sourceLanguage={sourceLanguage}
        translated={result.translated}
        severity={concern.severity}
        englishGloss={englishGloss}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
